import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';

/**
 * The one sign-out control the SPA has, for every place that shows one:
 * the header's desktop button, the phone menu, the pending-approval page.
 *
 * What it does depends on where the tab is:
 *
 *  * Outside a login-as session ("Log ind som"): today's sign-out —
 *    `logout()` (which, for an admin, also ends the login-as session
 *    another tab of this browser is in) and back to the start page.
 *  * INSIDE a session: it ends the session and goes back to where the
 *    session was started (the banner's transition does the navigating).
 *    It never signs the admin out of their own login — `logout()` itself
 *    refuses to, the check here only keeps the start-page redirect from
 *    racing the return. The control reads "Afslut" instead of "Log ud",
 *    so what it does is what it says.
 *
 * The decision is taken when the control is used, from the store — not
 * from a value captured at render — so a handler held over from before a
 * session began can never sign the admin out.
 */
export function useSignOut(): {
  /** Do it. */
  signOut: () => void;
  /** This tab is in a login-as session: `signOut` ends that, not the login. */
  impersonating: boolean;
  /** The control's text: "Log ud", or "Afslut" inside a session. */
  label: string;
} {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const impersonating = useAuthStore((s) => s.impersonation !== null);

  const signOut = useCallback(() => {
    const auth = useAuthStore.getState();
    if (auth.impersonation) {
      void auth.endImpersonation();
      return;
    }
    auth.logout();
    navigate('/', { replace: true });
  }, [navigate]);

  return {
    signOut,
    impersonating,
    label: impersonating ? t('impersonation.end') : t('auth.signOut'),
  };
}
